import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import {
  Badge,
  Button,
  CalendarIcon,
  Card,
  LinkChainIcon,
  NumberInput,
  SearchIcon,
  TextInput,
  XIcon,
} from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ConditionalFormattingRule } from '../../../services/conditionalFormattingService';
import { ACCOUNTING_TABLE_DEFAULTS, acctTableHeader } from '../shared/typography';
import { formatReportAmountAlways } from '../reports/shared/reportUtils';
import {
  LinkPickerResponse,
  LinkPickerSelection,
  normalizePickerRecords,
  PickerRecord,
  pickOldestRecordsUntilAmount,
  rowOpenBalance,
  toLinkPickerSelection,
} from './linkPickerNormalize';

export type { LinkPickerSelection };

type PickerTableRow = PickerRecord & { _selected: boolean; _payAmount: string };

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Default Pay now = line debit local (not aggregate link balance). */
const defaultPayAmount = (row: PickerRecord): number => {
  if (row.debitLocal > 0.005) return round2(row.debitLocal);
  return rowOpenBalance(row);
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyId: number | null;
  accountId: number | null;
  asOfDate?: string;
  value?: string;
  /** When set, shows “Select oldest until amount” using this bank/pay target. */
  targetAmount?: number | null;
  onSelect: (selections: LinkPickerSelection[]) => void;
};

const formatKartelaAmount = (value: number) => (value === 0 ? '' : formatReportAmountAlways(value));

const emptyFooterColumn = {
  footerType: 'custom' as const,
  footerRender: () => null,
};

const PICKER_ROW_SELECTED_RULE: ConditionalFormattingRule = {
  id: -1,
  userId: 0,
  moduleName: 'link-picker',
  columnKey: '_selected',
  name: 'Selected row',
  scope: 'row',
  condition: { type: 'column', columnKey: '_selected', operator: 'eq', value: true },
  style: {
    backgroundColor: 'bg-indigo-50 dark:bg-indigo-950/40',
    borderColor: 'ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700',
  },
  isActive: true,
  priority: 1,
  isDefault: false,
  createdAt: '',
  updatedAt: '',
};

export const OpenItemLinkPickerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  companyId,
  accountId,
  asOfDate,
  value,
  targetAmount,
  onSelect,
}) => {
  const { colors } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [payAmounts, setPayAmounts] = useState<Record<number, string>>({});
  const hasTargetAmount = targetAmount != null && targetAmount > 0.005;

  const effectiveAsOf = asOfDate || new Date().toISOString().slice(0, 10);

  const pickerUrl =
    isOpen && companyId && accountId
      ? `/accounting/open-items/link-picker?companyId=${companyId}&accountId=${accountId}&asOfDate=${effectiveAsOf}`
      : null;

  const { data, isLoading, mutate } = useSWR<LinkPickerResponse>(pickerUrl, fetcher, {
    revalidateOnMount: true,
    dedupingInterval: 0,
  });

  useEffect(() => {
    if (!isOpen || !pickerUrl) return;
    void mutate();
  }, [isOpen, pickerUrl, mutate]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setSelectedIds(new Set());
    setPayAmounts({});
  }, [isOpen, value]);

  const q = searchTerm.trim().toLowerCase();

  const allRecords = useMemo(() => normalizePickerRecords(data), [data]);

  const records = useMemo(() => {
    if (!q) return allRecords;
    return allRecords.filter((row) =>
      (row.linkNumber?.toLowerCase().includes(q) ?? false)
      || row.narrative.toLowerCase().includes(q)
      || (row.docCode?.toLowerCase().includes(q) ?? false)
      || (row.currencyCode?.toLowerCase().includes(q) ?? false),
    );
  }, [allRecords, q]);

  const selectedRecords = useMemo(
    () => allRecords.filter((row) => selectedIds.has(row.id)),
    [allRecords, selectedIds],
  );

  const tableRows = useMemo<PickerTableRow[]>(
    () => records.map((row) => ({
      ...row,
      _selected: selectedIds.has(row.id),
      _payAmount: payAmounts[row.id] ?? '',
    })),
    [payAmounts, records, selectedIds],
  );

  const debitTotal = useMemo(
    () => tableRows.reduce((sum, row) => sum + row.debitLocal, 0),
    [tableRows],
  );

  const debitUsdTotal = useMemo(
    () => tableRows.reduce((sum, row) => sum + row.debitUSD, 0),
    [tableRows],
  );

  /** Strict sum of Pay now values for selected rows (no debit fallback). */
  const selectedPayTotal = useMemo(() => {
    let sum = 0;
    selectedIds.forEach((id) => {
      const raw = payAmounts[id];
      if (raw == null || String(raw).trim() === '') return;
      const pay = Number(String(raw).replace(/,/g, '').trim());
      if (Number.isFinite(pay)) sum += pay;
    });
    return round2(sum);
  }, [payAmounts, selectedIds]);

  const applySelectIds = useCallback((ids: number[], recordsForDefaults: PickerRecord[]) => {
    const byId = new Map(recordsForDefaults.map((r) => [r.id, r]));
    setSelectedIds(new Set(ids));
    setPayAmounts((prev) => {
      const next: Record<number, string> = {};
      for (const id of ids) {
        if (prev[id] != null && prev[id] !== '') {
          next[id] = prev[id];
          continue;
        }
        const row = byId.get(id);
        const open = row ? defaultPayAmount(row) : 0;
        if (open > 0.005) next[id] = String(open);
      }
      return next;
    });
  }, []);

  const toggleRecord = useCallback((recordId: number, additive = false) => {
    const row = allRecords.find((r) => r.id === recordId);
    setSelectedIds((prev) => {
      const next = additive ? new Set(prev) : new Set<number>();
      const removing = prev.has(recordId) && (additive || prev.size === 1);
      if (removing) {
        next.delete(recordId);
      } else if (additive) {
        next.add(recordId);
      } else {
        next.clear();
        next.add(recordId);
      }

      setPayAmounts((prevPay) => {
        const nextPay: Record<number, string> = additive ? { ...prevPay } : {};
        if (!additive) {
          // Single-select: keep only the newly selected (or none if removing)
          if (!removing && row) {
            const open = defaultPayAmount(row);
            nextPay[recordId] = prevPay[recordId] || (open > 0.005 ? String(open) : '');
            if (!nextPay[recordId]) delete nextPay[recordId];
          }
          return nextPay;
        }
        if (removing) {
          delete nextPay[recordId];
          return nextPay;
        }
        if (row && (nextPay[recordId] == null || nextPay[recordId] === '')) {
          const open = defaultPayAmount(row);
          if (open > 0.005) nextPay[recordId] = String(open);
        }
        return nextPay;
      });

      return next;
    });
  }, [allRecords]);

  const setPayAmountForRow = useCallback((row: PickerRecord, value: string) => {
    setPayAmounts((prev) => ({ ...prev, [row.id]: value }));
    const amount = Number(value);
    if (value !== '' && Number.isFinite(amount) && amount > 0.005) {
      setSelectedIds((prev) => {
        if (prev.has(row.id)) return prev;
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
    }
  }, []);

  const columns = useMemo((): Column<PickerTableRow>[] => [
    {
      key: 'select',
      header: '',
      width: '2.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <input
          type="checkbox"
          checked={row._selected}
          onChange={(e) => {
            e.stopPropagation();
            toggleRecord(row.id, true);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.docCode ?? row.narrative}`}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
      ),
    },
    {
      key: 'postingDate',
      header: 'Date',
      width: '6.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {row.postingDate ? formatDateGlobal(row.postingDate) : '—'}
        </span>
      ),
    },
    {
      key: 'docCode',
      header: 'Doc',
      width: '4.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.textMuted)}>
          {row.docCode ?? '—'}
        </span>
      ),
    },
    {
      key: 'narrative',
      header: 'Narrative',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm truncate block', colors.text)} title={row.narrative}>
          {row.narrative}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due',
      width: '6.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {row.dueDate ? formatDateGlobal(row.dueDate) : ''}
        </span>
      ),
    },
    {
      key: 'linkNumber',
      header: 'Link',
      width: '7rem',
      ...emptyFooterColumn,
      render: (_v, row) => {
        const hasLink = Boolean(row.linkNumber?.trim());
        return hasLink ? (
          <Badge variant="info" className="font-normal tabular-nums">
            {row.linkNumber}
          </Badge>
        ) : (
          <Badge variant="secondary" className="font-normal">
            No link
          </Badge>
        );
      },
    },
    {
      key: 'currencyCode',
      header: 'Cur',
      width: '3.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={colors.text}>{row.currencyCode ?? '—'}</span>
      ),
    },
    {
      key: 'debitLocal',
      header: 'Debit local',
      align: 'right',
      width: '7.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', row.debitLocal > 0 && 'text-amber-700 dark:text-amber-400 font-medium')}>
          {formatKartelaAmount(row.debitLocal)}
        </span>
      ),
    },
    {
      key: 'payAmount',
      header: 'Pay now',
      align: 'right',
      width: '11rem',
      ...emptyFooterColumn,
      render: (_v, row) => {
        const open = defaultPayAmount(row);
        const pay = Number(row._payAmount);
        const isPartial = row._selected && Number.isFinite(pay) && pay > 0.005 && open > 0.005 && pay < open - 0.01;
        const isOver = row._selected && Number.isFinite(pay) && open > 0.005 && pay > open + 0.01;
        return (
          <div className="min-w-[6.5rem] w-full" onClick={(e) => e.stopPropagation()}>
            <NumberInput
              value={row._payAmount}
              onChange={(e) => {
                setPayAmountForRow(row, String(e.target.value ?? ''));
              }}
              className={cn(
                'text-right tabular-nums h-10 text-base font-semibold tracking-wide',
                isPartial && 'border-amber-400 focus-visible:ring-amber-400',
                isOver && 'border-red-500 focus-visible:ring-red-500',
              )}
              allowDecimals
              placeholder={open > 0.005 ? String(open) : ''}
            />
          </div>
        );
      },
    },
    {
      key: 'debitUSD',
      header: 'Debit USD',
      align: 'right',
      width: '6.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {formatKartelaAmount(row.debitUSD)}
        </span>
      ),
    },
  ], [colors.text, colors.textMuted, setPayAmountForRow, toggleRecord]);

  const toggleAllVisible = () => {
    if (selectedRecords.length === records.length && records.length > 0) {
      setSelectedIds(new Set());
      setPayAmounts({});
      return;
    }
    applySelectIds(records.map((row) => row.id), records);
  };

  const selectOldestUntilAmount = () => {
    if (!hasTargetAmount || !targetAmount) return;
    const picked = pickOldestRecordsUntilAmount(allRecords, targetAmount);
    if (picked.length === 0) return;
    setSearchTerm('');
    setSelectedIds(new Set(picked.map((p) => p.record.id)));
    const nextPay: Record<number, string> = {};
    for (const item of picked) {
      // Keep two decimals so Pay total matches bank target cleanly
      nextPay[item.record.id] = item.payAmount.toFixed(2);
    }
    setPayAmounts(nextPay);
  };

  const confirmSelection = () => {
    if (selectedRecords.length === 0) return;
    const selections = selectedRecords.map((row) => {
      const open = defaultPayAmount(row);
      const raw = payAmounts[row.id];
      const pay = raw != null && raw !== '' && Number.isFinite(Number(raw))
        ? round2(Number(raw))
        : open;
      return toLinkPickerSelection(
        { ...row, linkBalance: open },
        pay > 0.005 ? pay : null,
      );
    }).filter((s) => (s.payAmount ?? s.openBalance ?? 0) > 0.005);
    if (selections.length === 0) return;
    onSelect(selections);
    onClose();
  };

  const allVisibleSelected = records.length > 0 && selectedRecords.length === records.length;

  const handleRowClick = useCallback((row: PickerTableRow) => {
    const clickEvent = window.event as MouseEvent | null;
    toggleRecord(row.id, Boolean(clickEvent?.ctrlKey || clickEvent?.metaKey));
  }, [toggleRecord]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalHeader>
        <div className="min-w-0 flex items-center gap-3 py-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 shrink-0">
            <LinkChainIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <ModalTitle>Select open items</ModalTitle>
            <p className={cn('text-sm mt-0.5', colors.textMuted)}>
              {hasTargetAmount
                ? `Target ${formatReportAmountAlways(targetAmount!)} — select fills Pay now; edit for partial, or use Oldest until amount.`
                : 'Select fills Pay now from debit local — edit the amount for a partial payment.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1 rounded-lg transition-colors shrink-0', colors.textSecondary, colors.interactiveHover)}
          aria-label="Close"
        >
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>

      <ModalContent className="!p-0 flex flex-col min-h-0">
        <div className="bg-indigo-700 dark:bg-indigo-900 px-6 py-4 shrink-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 space-y-3">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-white/60">Company</p>
                <p className="text-sm font-semibold text-white truncate">
                  {data?.company ? `${data.company.name}` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-white/60">Account</p>
                <p className="text-sm font-semibold text-white truncate">
                  {data?.account?.name ?? data?.account?.combLevel ?? data?.account?.code ?? '—'}
                </p>
                
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Badge variant="secondary" className="bg-white/15 text-white border-white/20 hover:bg-white/15 font-normal">
                Open payables
              </Badge>
              <div className="inline-flex items-center gap-1.5 text-xs text-white/80 tabular-nums">
                <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                As of {formatDateGlobal(data?.asOfDate ?? effectiveAsOf)}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-3 flex-1 min-h-0 flex flex-col">
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="flex-1 min-w-[14rem]">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search link, doc, narrative…"
                prefix={<SearchIcon className="h-4 w-4" />}
                showClearButton={Boolean(searchTerm)}
              />
            </div>
            {!isLoading && records.length > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={toggleAllVisible}>
                {allVisibleSelected ? 'Clear selection' : 'Select all'}
              </Button>
            )}
            {!isLoading && hasTargetAmount && allRecords.length > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={selectOldestUntilAmount}>
                Oldest until {formatReportAmountAlways(targetAmount!)}
              </Button>
            )}
            {!isLoading && (
              <Badge variant="secondary" className="font-normal tabular-nums h-9 px-3 flex items-center">
                {selectedRecords.length > 0
                  ? `${selectedRecords.length} / ${records.length} selected`
                  : `${records.length} record${records.length === 1 ? '' : 's'}`}
              </Badge>
            )}
          </div>

          <Card className={cn('flex flex-col min-h-0 flex-1 overflow-hidden p-0', colors.surface)}>
            <div className={cn('px-4 py-2.5 border-b flex items-center justify-between gap-3 shrink-0', colors.border)}>
              <div>
                <p className={cn('text-sm font-medium', colors.text)}>Payable items</p>
                <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                  Select fills Pay now from debit local · edit for partial
                </p>
              </div>
              {!isLoading && tableRows.length > 0 && (
                <div className="hidden sm:flex items-center gap-4 text-xs shrink-0">
                  <div className="text-right">
                    <p className={cn('uppercase tracking-wide', colors.textMuted)}>Debit L.</p>
                    <p className={cn('font-semibold tabular-nums text-amber-700 dark:text-amber-400')}>
                      {formatReportAmountAlways(debitTotal)}
                    </p>
                  </div>
                  <div className={cn('w-px h-8', colors.border, 'bg-current opacity-20')} />
                  <div className="text-right">
                    <p className={cn('uppercase tracking-wide', colors.textMuted)}>Debit USD</p>
                    <p className={cn('font-semibold tabular-nums', colors.text)}>
                      {formatReportAmountAlways(debitUsdTotal)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 min-h-0">
              <DataTable
                {...ACCOUNTING_TABLE_DEFAULTS}
                density="compact"
                data={tableRows}
                columns={columns}
                keyExtractor={(row) => row.id}
                loading={isLoading}
                emptyMessage="No open payable items found."
                stickyHeader
                conditionalFormattingRules={[PICKER_ROW_SELECTED_RULE]}
                onRowClick={handleRowClick}
                tableWrapperClassName="w-full block min-w-full overflow-x-auto max-h-[min(50vh,28rem)] overflow-y-auto dashboard-scrollbar"
                containerClassName="border-0 shadow-none rounded-none h-full"
                tableClassName={cn('w-full min-w-[68rem] table-fixed divide-y', colors.border)}
                theadClassName={cn('bg-slate-50/95 dark:bg-slate-800/90', colors.border)}
                thClassName={acctTableHeader}
              />
            </div>

            {!isLoading && tableRows.length > 0 && (
              <div
                className={cn(
                  'px-4 py-2.5 border-t flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-xs shrink-0',
                  colors.border,
                  'bg-slate-50/80 dark:bg-slate-900/40',
                )}
              >
                <span className={cn('font-medium uppercase tracking-wide', colors.textMuted)}>
                  Totals ({records.length})
                </span>
                <span className={cn('tabular-nums', colors.textMuted)}>
                  Debit L.{' '}
                  <span className="font-semibold text-amber-700 dark:text-amber-400">
                    {formatReportAmountAlways(debitTotal)}
                  </span>
                </span>
                <span className={cn('tabular-nums', colors.textMuted)}>
                  Debit USD{' '}
                  <span className={cn('font-semibold', colors.text)}>
                    {formatReportAmountAlways(debitUsdTotal)}
                  </span>
                </span>
              </div>
            )}
          </Card>
        </div>
      </ModalContent>

      <ModalFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          {selectedRecords.length === 0 && (
            <p className={cn('text-sm', colors.textMuted)}>
              Select invoices — Pay now fills from debit local; edit for a partial.
            </p>
          )}
          {selectedRecords.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <div>
                <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>Selected</p>
                <p className={cn('text-sm font-semibold tabular-nums', colors.text)}>
                  {selectedRecords.length} record{selectedRecords.length === 1 ? '' : 's'}
                </p>
              </div>
              <div>
                <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                  Pay total
                </p>
                <p
                  className="text-sm font-semibold tabular-nums text-indigo-700 dark:text-indigo-300"
                  title="Sum of Pay now amounts"
                >
                  {formatReportAmountAlways(selectedPayTotal)}
                </p>
              </div>
              {hasTargetAmount && (
                <div>
                  <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>Target</p>
                  <p className={cn(
                    'text-sm font-semibold tabular-nums',
                    Math.abs(selectedPayTotal - (targetAmount ?? 0)) <= 0.01
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-amber-700 dark:text-amber-400',
                  )}>
                    {formatReportAmountAlways(targetAmount!)}
                  </p>
                </div>
              )}
              {selectedRecords.length === 1 && (
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>Item</p>
                  <p className={cn('text-sm truncate', colors.text)} title={selectedRecords[0].narrative}>
                    {selectedRecords[0].linkNumber
                      ? `Link ${selectedRecords[0].linkNumber} — ${selectedRecords[0].narrative}`
                      : `Doc ${selectedRecords[0].docCode ?? '—'} — ${selectedRecords[0].narrative}`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirmSelection}
            disabled={selectedRecords.length === 0}
          >
            Use selected{selectedRecords.length > 0 ? ` (${selectedRecords.length})` : ''}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
